import { supabase } from '@/lib/supabase';
import { GameStats, PlayerStats, PlayerGameLog, VideoStats, VideoGameStats } from '@/types/stats';
import { getPlayerImageUrl } from '@/utils/playerUtils';
import { isHomeGame } from '@/utils/teamUtils';

export class SupabaseStatsService {
    /**
     * Fetch all stats data from Supabase including games, player stats, and game logs.
     * Mit seasonId werden alle Quellen auf diese Saison gefiltert; ohne
     * seasonId (z. B. solange die Saison-Migration fehlt) wird ungefiltert
     * geladen — das entspricht dem bisherigen Verhalten.
     */
    static async fetchAllStatsData(seasonId?: number | null): Promise<{
        games: GameStats[];
        playerStats: PlayerStats[];
        gameLogs: PlayerGameLog[];
        videoStats: VideoStats[];
    }> {
        try {
            // 1. Fetch Games
            let gamesQuery = supabase
                .from('games')
                .select('*')
                .order('game_date', { ascending: false });
            if (seasonId != null) {
                gamesQuery = gamesQuery.eq('season_id', seasonId);
            }
            const { data: gamesData, error: gamesError } = await gamesQuery;

            if (gamesError) throw gamesError;

            // Map games to generic GameStats type
            const games: GameStats[] = (gamesData || []).map((game) => ({
                gameNumber: game.tsv_game_number ? parseInt(game.tsv_game_number) : parseInt(game.game_id), // Use TSV Game Number (1-N) if available, else Match ID
                date: game.game_date ? new Date(game.game_date).toLocaleDateString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                }) : '',
                homeTeam: game.home_team_name,
                awayTeam: game.away_team_name,
                finalScore: game.home_score !== null && game.away_score !== null
                    ? `${game.home_score}:${game.away_score}`
                    : '-:-',
                q1Score: game.quarter_scores?.first_quarter_home !== undefined
                    ? `${game.quarter_scores.first_quarter_home}:${game.quarter_scores.first_quarter_away}`
                    : '-',
                halfTimeScore: game.quarter_scores?.halftime_home !== undefined
                    ? `${game.quarter_scores.halftime_home}:${game.quarter_scores.halftime_away}`
                    : '-',
                q3Score: game.quarter_scores?.third_quarter_home !== undefined
                    ? `${game.quarter_scores.third_quarter_home}:${game.quarter_scores.third_quarter_away}`
                    : '-',
                boxScoreUrl: game.box_score_url,
                gameId: game.game_id,
            }));

            // 1.5 Fetch Video Projects to populate video fields
            let videoProjectsQuery = supabase
                .from('video_projects')
                .select('*');
            if (seasonId != null) {
                videoProjectsQuery = videoProjectsQuery.eq('season_id', seasonId);
            }
            const { data: videoProjects, error: videoError } = await videoProjectsQuery;

            if (videoError) {
                console.warn('Error fetching video_projects:', videoError);
            } else if (videoProjects) {
                // Pre-group videos by game number for O(1) lookup
                const videosByGame = new Map<number, any[]>();
                videoProjects.forEach((vp: any) => {
                    // Ensure tsv_game_number is treated as number to match game.gameNumber
                    const gameNum = typeof vp.tsv_game_number === 'string'
                        ? parseInt(vp.tsv_game_number)
                        : vp.tsv_game_number;

                    if (!isNaN(gameNum)) {
                        if (!videosByGame.has(gameNum)) {
                            videosByGame.set(gameNum, []);
                        }
                        videosByGame.get(gameNum)!.push(vp);
                    }
                });

                // Map videos to games using the map
                games.forEach(game => {
                    const gameVideos = videosByGame.get(game.gameNumber);

                    if (gameVideos && gameVideos.length > 0) {
                        // Sort by video_index
                        gameVideos.sort((a: any, b: any) => a.video_index - b.video_index);

                        // Populate youtubeLink (backwards compatibility - use first video)
                        const firstVideo = gameVideos[0];
                        const firstLink = firstVideo.video_id
                            ? (firstVideo.playlist_id
                                ? `https://www.youtube.com/watch?v=${firstVideo.video_id}&list=${firstVideo.playlist_id}`
                                : `https://www.youtube.com/watch?v=${firstVideo.video_id}`)
                            : (firstVideo.playlist_id
                                ? `https://www.youtube.com/playlist?list=${firstVideo.playlist_id}`
                                : '');

                        game.youtubeLink = firstLink;

                        // Populate youtubeLinks
                        game.youtubeLinks = gameVideos.map((vp: any) =>
                            vp.video_id
                                ? (vp.playlist_id
                                    ? `https://www.youtube.com/watch?v=${vp.video_id}&list=${vp.playlist_id}`
                                    : `https://www.youtube.com/watch?v=${vp.video_id}`)
                                : (vp.playlist_id
                                    ? `https://www.youtube.com/playlist?list=${vp.playlist_id}`
                                    : '')
                        );

                        // Populate videoData
                        game.videoData = gameVideos.map((vp: any) => ({
                            link: vp.video_id
                                ? (vp.playlist_id
                                    ? `https://www.youtube.com/watch?v=${vp.video_id}&list=${vp.playlist_id}`
                                    : `https://www.youtube.com/watch?v=${vp.video_id}`)
                                : (vp.playlist_id
                                    ? `https://www.youtube.com/playlist?list=${vp.playlist_id}`
                                    : ''),
                            events: vp.data?.events || [],
                            players: vp.data?.players || [],
                            videoIndex: vp.video_index,
                            metadata: vp.data?.metadata // Map metadata
                        }));
                    }
                });
            }

            // 2. Fetch Player Stats (Season Totals)
            // Using the view created in migration
            let playersQuery = supabase
                .from('player_season_totals')
                .select('*');
            if (seasonId != null) {
                playersQuery = playersQuery.eq('season_id', seasonId);
            }
            const { data: playersData, error: playersError } = await playersQuery;

            if (playersError) {
                // Fallback for when views might not exist yet or error out
                console.warn('Error fetching player_season_totals, returning empty stats', playersError);
            }

            const playerStats: PlayerStats[] = (playersData || []).map((p: any) => ({
                id: p.player_slug,
                internalId: p.internal_id, // Added internal UUID for video stats matching
                firstName: p.first_name,
                lastName: p.last_name,
                imageUrl: getPlayerImageUrl(p.first_name, p.last_name),
                jerseyNumber: p.jersey_number,
                position: p.position,
                height: p.height,
                bio: p.bio,
                gamesPlayed: p.games_played,
                minutesPerGame: Number(p.minutes_per_game),
                pointsPerGame: Number(p.points_per_game),
                threePointersPerGame: Number(p.three_pointers_per_game),
                foulsPerGame: Number(p.fouls_per_game),
                freeThrowsMadePerGame: Number(p.free_throws_made_per_game),
                freeThrowAttemptsPerGame: Number(p.free_throw_attempts_per_game),
                freeThrowPercentage: p.free_throw_percentage,
                pointsPer40: Number(p.points_per_40),
                threePointersPer40: Number(p.three_pointers_per_40),
                foulsPer40: Number(p.fouls_per_40),
                birthDate: p.birth_date // Added birthDate mapping
            }));

            // 3. Fetch Game Logs
            let logsQuery = supabase
                .from('player_game_logs')
                .select('*');
            if (seasonId != null) {
                logsQuery = logsQuery.eq('season_id', seasonId);
            }
            const { data: logsData, error: logsError } = await logsQuery;

            if (logsError) {
                console.warn('Error fetching player_game_logs', logsError);
            }

            // Create a map of games by game_id for O(1) lookup
            const gamesById = new Map<string, any>();
            if (gamesData) {
                gamesData.forEach(game => {
                    gamesById.set(game.game_id, game);
                });
            }

            const gameLogs: PlayerGameLog[] = (logsData || []).map((log: any) => {
                const game = gamesById.get(log.game_id);
                // game_type kommt aus der View; Fallback über den Spiel-Datensatz
                const gameType = log.game_type
                    ?? (isHomeGame(game ?? {}) ? 'Heim' : 'Auswärts');

                const gameNumber = game?.tsv_game_number ? parseInt(game.tsv_game_number) : parseInt(log.game_id);

                return {
                    playerId: log.player_slug,
                    gameNumber,
                    minutesPlayed: log.minutes_played || 0,
                    points: log.points || 0,
                    twoPointers: log.two_pointers || 0,
                    threePointers: log.three_pointers || 0,
                    freeThrowsMade: log.free_throws_made || 0,
                    freeThrowAttempts: log.free_throw_attempts || 0,
                    freeThrowPercentage: log.free_throw_percentage || '0%',
                    fouls: log.fouls || 0,
                    pointsPer40: Number(log.points_per_40) || 0,
                    freeThrowAttemptsPer40: Number(log.free_throw_attempts_per_40) || 0,
                    foulsPer40: Number(log.fouls_per_40) || 0,
                    threePointersPer40: Number(log.three_pointers_per_40) || 0,
                    gameType
                };
            });

            // 4. Fetch Video Stats (New)
            const { data: videoData, error: videoStatsError } = await supabase
                .from('player_video_stats')
                .select('*');

            if (videoStatsError) {
                console.warn('Error fetching player_video_stats', videoStatsError);
            }

            const videoStats: VideoStats[] = (videoData || []).map((v: any) => ({
                id: v.id,
                playerId: v.player_id,
                gameNumber: v.game_number,
                twoPointersMade: v.two_pointers_made,
                twoPointersAttempted: v.two_pointers_attempted,
                threePointersMade: v.three_pointers_made,
                threePointersAttempted: v.three_pointers_attempted,
                freeThrowsMade: v.free_throws_made || 0,
                freeThrowsAttempted: v.free_throws_attempted || 0,
                fouls: v.fouls || 0,
                totalPoints: v.total_points || 0,
                steals: v.steals,
                blocks: v.blocks,
                assists: v.assists,
                rebounds: v.rebounds,
                turnovers: v.turnovers
            }));

            return {
                games,
                playerStats,
                gameLogs,
                videoStats
            };

        } catch (error) {
            console.error('Error in fetchAllStatsData:', error);
            throw error;
        }
    }

    /**
     * Save video stats to the database (Upsert)
     */
    static async saveVideoStats(stats: VideoStats[]): Promise<void> {
        try {
            // Transform to snake_case for Supabase
            const dbStats = stats.map(s => ({
                player_id: s.playerId,
                game_number: s.gameNumber,
                two_pointers_made: s.twoPointersMade,
                two_pointers_attempted: s.twoPointersAttempted,
                three_pointers_made: s.threePointersMade,
                three_pointers_attempted: s.threePointersAttempted,
                free_throws_made: s.freeThrowsMade,
                free_throws_attempted: s.freeThrowsAttempted,
                fouls: s.fouls,
                total_points: s.totalPoints,
                steals: s.steals,
                blocks: s.blocks,
                assists: s.assists,
                rebounds: s.rebounds,
                turnovers: s.turnovers
            }));

            const { error } = await supabase
                .from('player_video_stats')
                .upsert(dbStats, { onConflict: 'player_id, game_number' });

            if (error) throw error;
        } catch (error) {
            console.error('Error saving video stats:', error);
            throw error;
        }
    }

    /**
     * Save video game stats (team-level) to the database (Upsert)
     */
    static async saveVideoGameStats(stats: VideoGameStats): Promise<void> {
        try {
            const dbStats = {
                game_number: stats.gameNumber,
                total_points: stats.totalPoints,
                total_assists: stats.totalAssists,
                total_rebounds: stats.totalRebounds,
                total_steals: stats.totalSteals,
                total_blocks: stats.totalBlocks,
                total_turnovers: stats.totalTurnovers,
                total_fouls: stats.totalFouls,
                team_fg_percentage: stats.teamFgPercentage,
                team_three_pt_percentage: stats.teamThreePtPercentage,
                team_ft_percentage: stats.teamFtPercentage,
                updated_at: new Date().toISOString()
            };

            const { error } = await supabase
                .from('video_game_stats')
                .upsert(dbStats, { onConflict: 'game_number' });

            if (error) throw error;
        } catch (error) {
            console.error('Error saving video game stats:', error);
            throw error;
        }
    }
}
